import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Instagram, Linkedin, Facebook, Youtube, Download, Star, CheckCircle2 } from "lucide-react";
import { AMANDA_PHOTO, SOCIAL, BRANDS, EXEC_CLIENTS, EDITORIAL, EXPERTISE } from "../data/mock";

export default function MediaKit() {
  return (
    <div data-testid="media-kit-page" className="bg-cream text-navy font-body min-h-screen">
      {/* Top bar */}
      <header className="bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="mediakit-home-link" className="inline-flex items-center gap-2 text-cream/85 hover:text-mustard transition-colors text-sm">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <div className="font-display text-xl md:text-2xl">
            Amanda <span className="italic text-mustard">Abbott</span>
            <span className="ml-3 text-[11px] uppercase tracking-[0.2em] text-cream/60">Media Kit</span>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="mediakit-print-button"
            className="hidden md:inline-flex items-center gap-2 text-cream/85 hover:text-mustard transition-colors text-sm"
          >
            <Download size={15} /> Print / PDF
          </button>
        </div>
      </header>

      {/* Cover */}
      <section className="relative py-16 md:py-24 border-b border-[#E5E0D8]">
        <div className="absolute top-0 left-0 right-0 h-1 bg-mustard" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow" data-testid="mediakit-eyebrow">Media Kit · 2026</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-tight mt-6">
              Amanda <span className="italic mustard-underline">Abbott</span>
            </h1>
            <p className="mt-6 font-display italic text-2xl md:text-3xl text-navy/85 leading-snug max-w-xl">
              Product designer, systems thinker, and brand strategist for founders,
              executives, and mission-led brands.
            </p>
            <p className="mt-5 text-lg text-ink-muted max-w-xl">
              Based on Australia's Sunshine Coast. Available worldwide for
              consulting, editorial, brand partnerships, and speaking engagements.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#partnerships" className="btn-primary">Partnership options ↓</a>
              <a href={`mailto:${SOCIAL.email}?subject=Media%20Kit%20Enquiry`} className="btn-outline" data-testid="mediakit-email-cta">
                Request full kit
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-[1.5px] border-mustard" aria-hidden="true" />
              <img
                src={AMANDA_PHOTO}
                alt="Amanda Abbott portrait"
                className="relative w-full h-[420px] md:h-[520px] object-cover"
                data-testid="mediakit-portrait"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Snapshot / positioning */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">Snapshot</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mt-6">
              A designer who's spent her career
              <span className="italic text-terracotta"> in the room where it happens.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-[17px] text-ink-muted leading-relaxed">
            <p>
              <strong className="text-navy">13+ years across brand, product, and technology.</strong> BS Industrial &amp; Product Design (University of Oregon, Ford Scholar 2008-2012). Top 5 of 5,000 · Seoul International "Design for All" Competition.
            </p>
            <p>
              <strong className="text-navy">Former Microsoft executive presentation designer</strong> supporting leaders at Xbox and Microsoft Health — including Phil Spencer, Panos Panay, Todd Holmdahl, Mike Angiulo, and Stuart Ashmun. Developed the Xbox 2015–2020 product roadmap comms and cross-functional visual language.
            </p>
            <p>
              <strong className="text-navy">Founder of LUXLA</strong> — a luxury accessories label stocked internationally and worn by Whitney Port, Emmy Rossum, Abigail Breslin, Plain White T's, and more. Designed collections for NastyGal, Wasteland, and Sofia Boutique. Styled for HauteLook, Stone Cold Fox, and Intermix.
            </p>
            <p>
              <strong className="text-navy">Advocate for neurodivergence and invisible illness.</strong> Speaks and writes openly about building a professional life around AuDHD and Ehlers-Danlos syndrome — and consults on accessible product and brand design.
            </p>
          </div>
        </div>
      </section>

      {/* Brands worked with */}
      <section className="py-14 md:py-16 bg-cream-alt border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <span className="eyebrow">Selected brands &amp; clients</span>
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="mediakit-brands">
            {BRANDS.map((b) => (
              <span key={b} className="px-4 py-2 border border-navy/25 text-navy font-display text-lg italic bg-cream">
                {b}
              </span>
            ))}
          </div>
          <div className="mt-8 pt-8 border-t border-[#E5E0D8]">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-mustard-deep" style={{ color: "#B47F2C" }}>
              Executive clients (Microsoft years)
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-navy/85 text-[15px]">
              {EXEC_CLIENTS.map((n) => (
                <span key={n}>· {n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Audience snapshot */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <span className="eyebrow">Audience</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mt-6">
                Reach across
                <span className="italic text-terracotta"> seven platforms.</span>
              </h2>
            </div>
            <p className="text-sm text-ink-muted italic max-w-xs">
              Full audience insights, demographics, and engagement rates available on
              request in the extended media kit.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mediakit-platforms">
            {[
              { name: "Instagram", handle: "@theamandaabbott", url: SOCIAL.instagram },
              { name: "LinkedIn", handle: "in/theamandaabbott", url: SOCIAL.linkedin },
              { name: "TikTok", handle: "@theamandaabbott", url: SOCIAL.tiktok },
              { name: "YouTube", handle: "@theamandaabbott", url: SOCIAL.youtube },
              { name: "Facebook", handle: "amandaabbottofficial", url: SOCIAL.facebook },
              { name: "Pinterest", handle: "@theamandaabbott", url: SOCIAL.pinterest },
              { name: "Coroflot", handle: "/amandamccombs", url: SOCIAL.coroflot },
              { name: "Newsletter", handle: "Coming soon", url: "/#newsletter" },
            ].map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" className="p-5 bg-white border border-[#E5E0D8] hover:border-terracotta transition-colors block">
                <div className="text-xs uppercase tracking-[0.16em] font-semibold text-navy">{p.name}</div>
                <div className="mt-1 text-terracotta text-[15px] font-medium truncate">{p.handle}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services / partnerships */}
      <section id="partnerships" className="py-16 md:py-20 bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <span className="eyebrow" style={{ color: "#D89C42" }}>
            <span style={{ background: "#D89C42" }} className="!w-7 !h-[1.5px] inline-block" />
            Ways to work together
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[52px] leading-tight mt-6">
            Partnership <span className="italic text-mustard">options.</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {EXPERTISE.map((s, i) => (
              <div key={s.id} data-testid={`mediakit-service-${s.id}`} className="border border-cream/20 p-7 bg-navy-soft" style={{ backgroundColor: "#122a4a" }}>
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-2xl md:text-3xl text-cream">{s.title}</h3>
                  <span className="font-display italic text-mustard text-sm">0{i + 1}</span>
                </div>
                <p className="mt-3 text-cream/75 text-[15px] leading-relaxed">{s.description}</p>
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-cream/90 text-[15px]">
                      <CheckCircle2 size={15} className="text-mustard flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="border border-mustard p-7 bg-mustard/10 md:col-span-2">
              <div className="flex items-start gap-4">
                <Star className="text-mustard flex-shrink-0" size={22} />
                <div>
                  <h3 className="font-display text-2xl md:text-3xl text-cream">Brand Partnership &amp; UGC</h3>
                  <p className="mt-2 text-cream/85 text-[15px] leading-relaxed">
                    Editorial + UGC content packages, brand ambassadorship, product seeding,
                    event appearances, and affiliate partnerships. Custom deliverables shaped
                    around your campaign brief and my audience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial samples */}
      <section className="py-16 md:py-20 bg-cream-alt border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <span className="eyebrow">Editorial samples</span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mt-6">
            Recent <span className="italic text-terracotta">work.</span>
          </h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5" data-testid="mediakit-gallery">
            {EDITORIAL.map((shot, i) => (
              <figure key={i} className="overflow-hidden bg-navy aspect-[3/4]">
                <img src={shot.src} alt={shot.alt} loading="lazy" className="w-full h-full object-cover" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-terracotta text-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">
            Interested in a full media kit
            <br />
            <span className="italic">with rates and audience insights?</span>
          </h2>
          <p className="mt-5 text-lg text-cream/90 max-w-xl mx-auto">
            Every partnership is different. Send a short brief and I'll reply with a
            tailored rate card, past campaign results, and next steps.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="mediakit-enquiry-cta"
              className="inline-flex items-center bg-navy text-cream px-7 py-4 font-medium tracking-wide hover:bg-mustard hover:text-navy transition-colors border-2 border-navy hover:border-mustard"
            >
              Send a brief →
            </Link>
            <a
              href={`mailto:${SOCIAL.email}?subject=Full%20Media%20Kit%20Request`}
              className="inline-flex items-center bg-transparent text-cream px-7 py-4 font-medium tracking-wide hover:bg-cream hover:text-terracotta transition-colors border-2 border-cream"
            >
              Email directly
            </a>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-navy text-cream/70 py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-display text-xl">
            Amanda <span className="italic text-mustard">Abbott</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-cream"><Instagram size={16} /></a>
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-cream"><Linkedin size={16} /></a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-cream"><Facebook size={16} /></a>
            <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-cream"><Youtube size={16} /></a>
            <a href={`mailto:${SOCIAL.email}`} aria-label="Email" className="hover:text-cream"><Mail size={16} /></a>
          </div>
          <span className="text-xs text-cream/50">© {new Date().getFullYear()} · amandaabbott.com</span>
        </div>
      </footer>
    </div>
  );
}
